// ===========================================
// COMPONENTE: EditarVencimientoDialog
// ===========================================
// Dialog para editar un vencimiento individual (fecha, monto, observaciones)

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Pencil, Loader2, AlertCircle } from 'lucide-react'
import { format } from 'date-fns'
import { getImpuestoColorClasses } from '@/lib/impuestoColorResolver'
import {
  useEditarVencimientoForm,
  getFieldError,
  type EditarVencimientoFormValues,
} from './forms'
import { useStore } from '@tanstack/react-form'
import { FormDialogContent } from '@/components/ui/form-dialog-content'

interface VencimientoParaEditar {
  id: string | null
  tipo_impuesto: string | null
  cliente_nombre: string | null
  periodo: string | null
  fecha_vencimiento: string | null
  fecha_original?: string | null
  monto_determinado?: number | null
  observaciones?: string | null
  motivo_prorroga?: string | null
}

interface EditarVencimientoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vencimiento: VencimientoParaEditar | null
  onConfirmar: (id: string, datos: Partial<EditarVencimientoFormValues>) => Promise<void>
}

export function EditarVencimientoDialog({
  open,
  onOpenChange,
  vencimiento,
  onConfirmar,
}: EditarVencimientoDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { form } = useEditarVencimientoForm({
    onSubmit: async (value: EditarVencimientoFormValues) => {
      if (!vencimiento?.id) return

      setIsSubmitting(true)
      try {
        await onConfirmar(vencimiento.id, {
          fecha_vencimiento: value.fecha_vencimiento,
          monto_determinado: value.monto_determinado,
          observaciones: value.observaciones,
          motivo_prorroga: value.motivo_prorroga,
        })
        form.reset()
        onOpenChange(false)
      } catch (error) {
        console.error('Error al editar vencimiento:', error)
      } finally {
        setIsSubmitting(false)
      }
    },
    defaultValues: {
      fecha_vencimiento: vencimiento?.fecha_vencimiento
        ? format(new Date(vencimiento.fecha_vencimiento), 'yyyy-MM-dd')
        : '',
      monto_determinado: vencimiento?.monto_determinado ?? null,
      observaciones: vencimiento?.observaciones ?? null,
    },
  })

  // Reset form when vencimiento changes
  useEffect(() => {
    if (vencimiento && open) {
      // Un solo `reset` con los valores: precargar define el punto de partida, no edita.
      // Campo por campo con `setFieldValue` el form nacía "sucio" y la guarda de ORR-392
      // pedía confirmación apenas se abría el diálogo.
      // `keepDefaultValues` conserva los valores por defecto del formulario: sin eso, `reset`
      // los reemplaza por los del evento y el siguiente re-render los detecta distintos de los
      // que declara el hook, así que vuelve a aplicar los iniciales y el formulario queda vacío.
      form.reset({
        fecha_vencimiento: vencimiento.fecha_vencimiento
          ? format(new Date(vencimiento.fecha_vencimiento), 'yyyy-MM-dd')
          : '',
        monto_determinado: vencimiento.monto_determinado ?? null,
        observaciones: vencimiento.observaciones ?? null,
        motivo_prorroga: vencimiento.motivo_prorroga ?? null,
      }, { keepDefaultValues: true })
    }
  }, [vencimiento?.id, open])

  const isDirty = useStore(form.store, (s) => s.isDirty)

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) form.reset()
    onOpenChange(newOpen)
  }

  if (!vencimiento) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <FormDialogContent
        isDirty={isDirty}
        onDiscard={() => handleOpenChange(false)} className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" />
            Editar Vencimiento
          </DialogTitle>
          <DialogDescription>
            Modificar datos del vencimiento fiscal
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted/50 p-3 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            {vencimiento.tipo_impuesto && (
              <Badge variant="outline" className={getImpuestoColorClasses(vencimiento.tipo_impuesto)}>
                {vencimiento.tipo_impuesto}
              </Badge>
            )}
            <span className="text-sm font-medium">{vencimiento.cliente_nombre}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            <span>Periodo: {vencimiento.periodo}</span>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          {vencimiento.fecha_original && (
            <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>
                Fecha original: {format(new Date(vencimiento.fecha_original), 'dd/MM/yyyy')}
                {vencimiento.motivo_prorroga && ` — ${vencimiento.motivo_prorroga}`}
              </span>
            </div>
          )}

          <form.Field name="fecha_vencimiento">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Fecha de Vencimiento *</Label>
                <Input
                  id={field.name}
                  type="date"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="motivo_prorroga">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Motivo de prórroga</Label>
                <Input
                  id={field.name}
                  placeholder="Ej: Resolución AFIP, feriado provincial..."
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  onBlur={field.handleBlur}
                />
                <p className="text-xs text-muted-foreground">
                  Opcional. Se registra si cambia la fecha de vencimiento.
                </p>
              </div>
            )}
          </form.Field>

          <form.Field name="monto_determinado">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Monto Determinado</Label>
                <Input
                  id={field.name}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={field.state.value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    field.handleChange(val === '' ? null : parseFloat(val))
                  }}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="observaciones">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Observaciones</Label>
                <Textarea
                  id={field.name}
                  placeholder="Notas adicionales..."
                  className="resize-none"
                  rows={2}
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </form>
      </FormDialogContent>
    </Dialog>
  )
}
